import { useState, useEffect, useCallback, ReactNode } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';
interface Toast { id: number; message: string; type: ToastType; }

let addToastExternal: ((message: string, type?: ToastType) => void) | null = null;
export const showToast = (message: string, type: ToastType = 'info') => { addToastExternal?.(message, type); };

const iconMap = { success: CheckCircle, error: XCircle, warning: AlertTriangle, info: Info };
const styleMap = {
    success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    warning: 'bg-amber-50 text-amber-800 border-amber-200',
    info: 'bg-sky-50 text-sky-800 border-sky-200',
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    let counter = 0;

    const addToast = useCallback((message: string, type: ToastType = 'info') => {
        const id = ++counter;
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    }, []);

    useEffect(() => { addToastExternal = addToast; return () => { addToastExternal = null; }; }, [addToast]);

    return (
        <>
            {children}
            <div className="fixed top-4 right-4 z-[60] space-y-2 max-w-sm">
                {toasts.map(toast => {
                    const Icon = iconMap[toast.type];
                    return (
                        <div key={toast.id} className={`flex items-start gap-2.5 p-3 rounded-xl border shadow-card animate-slide-up ${styleMap[toast.type]}`}>
                            <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm flex-1">{toast.message}</p>
                            <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))} className="p-0.5 rounded-md hover:bg-black/5 cursor-pointer">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </>
    );
};
